import { ArrowRight, ChevronRight, Minus, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDate } from '../../lib/utils';
import { useCurrencyStore } from '../../stores/currency';
import { Card } from './card';
import { InitialsAvatar } from './initials-avatar';

/* ── Types ── */

export type ActivityItem = {
	id: string;
	action: string;
	message?: string | null;
	actorId?: string | null;
	actorRole?: string | null;
	adminUser?: { firstName: string; lastName: string } | null;
	organizationUser?: { firstName: string; lastName: string } | null;
	metadata?: Record<string, unknown> | null;
	createdAt?: string | null;
};

export type ActivityNodeStyle = {
	icon: React.ReactNode;
	fg: string;
	ring: string;
	bg: string;
};

type ChangeOp = 'added' | 'removed' | 'changed';

type ChangeItem = {
	label: string;
	type: string;
	op?: ChangeOp;
	/** Path of the entity this change belongs to, e.g. product › quantity tier. */
	scope?: unknown;
	/** Unit for `type: 'range'` values, e.g. "days". */
	unit?: string;
	from?: unknown;
	to?: unknown;
	added?: unknown[];
	removed?: unknown[];
};

type ScopeSegment = {
	label: string;
	/** Present only when the backend sends typed scope segments. */
	kind?: string;
};

type ScopeNode = {
	key: string;
	label: string;
	kind?: string;
	/** The entity was created by the event being rendered. */
	isNew: boolean;
	/** The entity was deleted by the event being rendered. */
	isRemoved?: boolean;
	fields: ChangeItem[];
	children: ScopeNode[];
};

/* ── Typed node ── */

function TypedNode({ style }: { style: ActivityNodeStyle }) {
	return (
		<div
			className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
			style={{
				background: style.bg,
				border: `0.5px solid ${style.ring}`,
				color: style.fg,
			}}
		>
			{style.icon}
		</div>
	);
}

/* ── Helpers ── */

function formatAction(action: string) {
	return action
		.replace(/_/g, ' ')
		.toLowerCase()
		.replace(/^\w/, (c) => c.toUpperCase());
}

function getActorName(activity: ActivityItem): string | null {
	const user = activity.adminUser ?? activity.organizationUser;
	if (user) return `${user.firstName} ${user.lastName}`;
	if (activity.actorRole) {
		return (
			activity.actorRole.charAt(0).toUpperCase() + activity.actorRole.slice(1).toLowerCase()
		);
	}
	return null;
}

function formatTimestamp(dateStr: string): string {
	const date = new Date(dateStr);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / 60000);

	if (date.toDateString() === now.toDateString()) {
		if (diffMins < 1) return 'Just now';
		if (diffMins < 60) return `${diffMins} min ago`;
		return `${Math.floor(diffMins / 60)}h ago`;
	}

	return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function getDateGroup(dateStr: string): string {
	const date = new Date(dateStr);
	const now = new Date();
	if (date.toDateString() === now.toDateString()) return 'Today';
	const yesterday = new Date(now);
	yesterday.setDate(yesterday.getDate() - 1);
	if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
	return formatDate(dateStr);
}

function formatDateValue(v: unknown): string {
	if (!v || typeof v !== 'string') return 'not set';
	return formatDate(v);
}

function formatPercentValue(v: unknown): string {
	// Percentages arrive as fixed-scale decimal strings ("10.0000"); drop the
	// trailing zeros so the row reads as a number rather than a database column.
	const n = Number(v);
	return Number.isFinite(n) ? `${n}%` : `${String(v ?? '—')}%`;
}

function formatRangeValue(v: unknown, unit?: string): string {
	if (!v || typeof v !== 'object') return '—';
	const { min, max } = v as { min?: unknown; max?: unknown };
	if (min == null && max == null) return '—';
	const suffix = unit ? ` ${unit}` : '';
	if (min == null || max == null) return `${String(min ?? max)}${suffix}`;
	return String(min) === String(max)
		? `${String(min)}${suffix}`
		: `${String(min)}–${String(max)}${suffix}`;
}

function formatCurrencyValue(v: unknown, selectedCurrency: string): string {
	// With `inCurrency`, the backend resolves cost changes to a plain number. Older
	// rows (or unresolved values) may still arrive as a { CUR: amount } price map:
	// take the selected currency from it, else its first entry — labelled with
	// THAT entry's currency, never the selected one it isn't in.
	let amount: number | null = null;
	let currency = selectedCurrency;
	if (typeof v === 'number') {
		amount = v;
	} else if (v && typeof v === 'object') {
		const map = v as Record<string, number>;
		if (map[selectedCurrency] != null) {
			amount = map[selectedCurrency];
		} else {
			const first = Object.entries(map)[0];
			if (first) [currency, amount] = first;
		}
	}
	if (amount == null) return '—';
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency,
		// Cost changes are per-piece prices: 2 decimals, up to 4 when needed.
		minimumFractionDigits: 2,
		maximumFractionDigits: 4,
	}).format(amount);
}

/* ── Change type renderers ── */

function ScalarArrow({ from, to }: { from?: string | null; to: string }) {
	// `from === undefined` means there is no prior state to show — the row's owner
	// was created by this very event — so the transition arrow is dropped entirely.
	if (from === undefined) {
		return <span className="font-mono text-xs leading-[1.3] text-text-1">{to}</span>;
	}

	return (
		<span className="inline-flex flex-wrap items-center gap-1.5 font-mono text-xs leading-[1.3]">
			{from ? (
				<span
					className="text-text-4 line-through"
					style={{ textDecorationColor: 'var(--line-3)' }}
				>
					{from}
				</span>
			) : (
				<span className="italic text-text-4">not set</span>
			)}
			<ArrowRight size={10} className="text-text-4" />
			<span className="text-text-1">{to}</span>
		</span>
	);
}

function TagChip({ value, mode }: { value: string | number; mode: 'add' | 'remove' }) {
	const isAdd = mode === 'add';
	return (
		<span
			className="inline-flex items-center gap-0.5 rounded-[var(--r-1)] font-mono text-2xs font-medium"
			style={{
				height: 19,
				padding: '0 6px',
				background: isAdd
					? 'color-mix(in srgb, var(--status-success) 10%, transparent)'
					: 'color-mix(in srgb, var(--status-danger) 10%, transparent)',
				color: isAdd ? 'var(--status-success)' : 'var(--status-danger)',
				border: `0.5px solid ${isAdd ? 'color-mix(in srgb, var(--status-success) 20%, transparent)' : 'color-mix(in srgb, var(--status-danger) 20%, transparent)'}`,
			}}
		>
			<span style={{ opacity: 0.85, fontWeight: 600 }}>{isAdd ? '+' : '−'}</span>
			{typeof value === 'number' ? value.toLocaleString() : value}
		</span>
	);
}

/** Marker slot for a scope header. Fixed width so headers align with or without a mark. */
function OpMark({ op }: { op?: ChangeOp }) {
	return (
		<span className="flex w-3 shrink-0 items-center justify-center">
			{op === 'added' && <Plus size={11} className="text-status-success" />}
			{op === 'removed' && <Minus size={11} className="text-status-danger" />}
		</span>
	);
}

/** One change as two grid cells: label, then value. */
function ChangeRow({
	change,
	currency,
	priorless,
}: {
	change: ChangeItem;
	currency: string;
	/** The owning entity was created by this event, so there is no "from" side. */
	priorless: boolean;
}) {
	const label = <dt className="text-xs text-text-3">{change.label}</dt>;
	const prior = (formatted: string | null) => (priorless ? undefined : formatted);

	switch (change.type) {
		case 'date':
			return (
				<>
					{label}
					<dd className="min-w-0">
						<ScalarArrow
							from={prior(change.from ? formatDateValue(change.from) : null)}
							to={formatDateValue(change.to)}
						/>
					</dd>
				</>
			);

		case 'tags': {
			const added = (change.added ?? []) as number[];
			const removed = (change.removed ?? []) as number[];
			if (added.length === 0 && removed.length === 0) return null;
			return (
				<>
					{label}
					<dd className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
						{added.map((n) => (
							<TagChip key={`a${n}`} value={n} mode="add" />
						))}
						{removed.map((n) => (
							<TagChip key={`r${n}`} value={n} mode="remove" />
						))}
					</dd>
				</>
			);
		}

		case 'currency':
			return (
				<>
					{label}
					<dd className="min-w-0">
						<ScalarArrow
							from={prior(
								change.from != null
									? formatCurrencyValue(change.from, currency)
									: null
							)}
							to={formatCurrencyValue(change.to, currency)}
						/>
					</dd>
				</>
			);

		case 'percentage':
			return (
				<>
					{label}
					<dd className="min-w-0">
						<ScalarArrow
							from={prior(
								change.from != null ? formatPercentValue(change.from) : null
							)}
							to={formatPercentValue(change.to)}
						/>
					</dd>
				</>
			);

		case 'range':
			return (
				<>
					{label}
					<dd className="min-w-0">
						<ScalarArrow
							from={prior(
								change.from != null
									? formatRangeValue(change.from, change.unit)
									: null
							)}
							to={formatRangeValue(change.to, change.unit)}
						/>
					</dd>
				</>
			);

		case 'added':
		case 'removed':
			return (
				<div
					className="flex flex-wrap items-center gap-1.5"
					style={{ gridColumn: '1 / -1' }}
				>
					{change.type === 'added' ? (
						<Plus size={11} className="text-status-success" style={{ opacity: 0.7 }} />
					) : (
						<Minus size={11} className="text-status-danger" style={{ opacity: 0.7 }} />
					)}
					<span className="text-xs text-text-2">{change.label}</span>
				</div>
			);

		case 'text':
		default:
			return (
				<>
					{label}
					<dd className="min-w-0">
						<ScalarArrow
							from={prior(change.from != null ? String(change.from) : null)}
							to={String(change.to ?? '')}
						/>
					</dd>
				</>
			);
	}
}

/* ── Scope grouping ── */

/**
 * `scope` arrives either as bare labels (`["test", "120 pcs"]`) or, where the
 * backend has been updated, as typed segments (`{ type, id, label }`). Both
 * normalise to the same shape so the tree builder doesn't care which it got.
 */
function normalizeScope(scope: unknown): ScopeSegment[] {
	if (!Array.isArray(scope)) return [];
	return scope
		.map((segment): ScopeSegment => {
			if (typeof segment === 'string') return { label: segment };
			if (segment && typeof segment === 'object') {
				const record = segment as Record<string, unknown>;
				const label = record.label ?? record.name;
				return {
					label: typeof label === 'string' ? label : '',
					kind: typeof record.type === 'string' ? record.type : undefined,
				};
			}
			return { label: '' };
		})
		.filter((segment) => segment.label.length > 0);
}

/**
 * Changes carry a `scope` naming the entity they belong to (a product, a
 * quantity tier within that product, …), and `type: 'entity'` rows describe
 * those scope nodes rather than any field on them. Flattening all of it into
 * one list leaves product and tier names rendered as valueless "not set →"
 * rows with their fields floating beside them, so rebuild the tree instead.
 */
function buildScopeTree(changes: ChangeItem[]): ScopeNode {
	const root: ScopeNode = { key: '', label: '', isNew: false, fields: [], children: [] };
	const byKey = new Map<string, ScopeNode>([['', root]]);

	const nodeFor = (path: ScopeSegment[]): ScopeNode => {
		const key = path.map((segment) => segment.label).join(' › ');
		const existing = byKey.get(key);
		if (existing) return existing;

		const parent = nodeFor(path.slice(0, -1));
		const segment = path[path.length - 1];
		const node: ScopeNode = {
			key,
			label: segment.label,
			kind: segment.kind,
			isNew: false,
			fields: [],
			children: [],
		};
		parent.children.push(node);
		byKey.set(key, node);
		return node;
	};

	for (const change of changes) {
		const path = normalizeScope(change.scope);
		if (change.type === 'entity') {
			// An entity change describes the scope node itself, not a field on it:
			// its own `scope` is its full path. Fall back to the label so a flat
			// payload without scopes still produces a named group.
			const node = nodeFor(path.length > 0 ? path : [{ label: change.label }]);
			if (change.op === 'added') node.isNew = true;
			if (change.op === 'removed') node.isRemoved = true;
		} else {
			nodeFor(path).fields.push(change);
		}
	}

	// A creation event has no prior state anywhere, so root-level fields drop
	// their arrows too — "not set → Sep 9, 2026" is noise on a brand new offer.
	root.isNew = changes.length > 0 && changes.every((change) => change.op === 'added');
	return root;
}

/** Aligned label/value grid for the fields belonging to one scope node. */
function FieldGrid({
	fields,
	currency,
	priorless,
}: {
	fields: ChangeItem[];
	currency: string;
	priorless: boolean;
}) {
	if (fields.length === 0) return null;

	return (
		<dl
			className="grid min-w-0 items-baseline gap-x-4 gap-y-1.5"
			style={{ gridTemplateColumns: 'max-content minmax(0, 1fr)' }}
		>
			{fields.map((change, i) => (
				<ChangeRow key={i} change={change} currency={currency} priorless={priorless} />
			))}
		</dl>
	);
}

/** One entity in the scope tree: its name, then its fields and nested entities. */
function ScopeGroup({
	node,
	depth,
	currency,
	inheritedNew,
}: {
	node: ScopeNode;
	depth: number;
	currency: string;
	inheritedNew: boolean;
}) {
	const priorless = node.isNew || inheritedNew;
	const hasBody = node.fields.length > 0 || node.children.length > 0;

	return (
		<div className="flex min-w-0 flex-col gap-1.5">
			<div className="flex min-w-0 items-center gap-1.5">
				<OpMark op={node.isRemoved ? 'removed' : node.isNew ? 'added' : undefined} />
				<span
					className={
						depth === 0
							? 'truncate text-xs font-medium text-text-1'
							: 'truncate font-mono text-xs text-text-2'
					}
					style={node.isRemoved ? { textDecoration: 'line-through' } : undefined}
				>
					{node.label}
				</span>
			</div>

			{hasBody && (
				<div
					className="flex min-w-0 flex-col gap-2"
					style={{
						marginLeft: 4,
						paddingLeft: 10.4,
						borderLeft: '1px solid var(--line-1)',
					}}
				>
					<FieldGrid fields={node.fields} currency={currency} priorless={priorless} />
					{node.children.map((child) => (
						<ScopeGroup
							key={child.key}
							node={child}
							depth={depth + 1}
							currency={currency}
							inheritedNew={priorless}
						/>
					))}
				</div>
			)}
		</div>
	);
}

/**
 * The flat form can only say one thing: a single chain of scopes, with every
 * field hanging off the deepest one. Anything else needs the tree or it loses
 * information — a sibling scope would vanish, or the breadcrumb would credit
 * the wrong owner for a field. Within that limit, prefer flat: a rail-and-marker
 * tree for one repriced tier is heavier than the fact deserves, and the message
 * already names the verb ("Added product").
 */
function shouldRenderAsTree(root: ScopeNode): boolean {
	const chain: ScopeNode[] = [];
	let node = root;
	while (node.children.length === 1) {
		node = node.children[0];
		chain.push(node);
	}

	// A branch below the chain would drop every sibling but the first.
	if (node.children.length > 1) return true;
	// Nothing scoped — the tree branch renders no children either.
	if (chain.length === 0) return true;
	// A bare scope node (an entity added or removed with no fields of its own)
	// only has somewhere to render its name in the tree.
	if (node.fields.length === 0) return true;
	// Fields above the deepest node would look like they belong to it.
	return chain.slice(0, -1).some((ancestor) => ancestor.fields.length > 0);
}

/** Path down to the one node carrying fields. Only meaningful in the flat case. */
function findFieldPath(root: ScopeNode): ScopeNode[] {
	const walk = (node: ScopeNode, trail: ScopeNode[]): ScopeNode[] | null => {
		if (node.fields.length > 0) return trail;
		for (const child of node.children) {
			const found = walk(child, [...trail, child]);
			if (found) return found;
		}
		return null;
	};
	for (const child of root.children) {
		const found = walk(child, [child]);
		if (found) return found;
	}
	return [];
}

/** "test › 120 pcs" — the event's scope, stated once above its fields. */
function ScopeCrumbs({ crumbs }: { crumbs: ScopeNode[] }) {
	return (
		<div className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-text-3">
			{crumbs.map((node, i) => (
				<span key={node.key} className="flex min-w-0 items-center gap-1.5">
					{i > 0 && <ChevronRight size={11} className="shrink-0 text-text-4" />}
					<span className={i === crumbs.length - 1 ? 'truncate text-text-2' : 'truncate'}>
						{node.label}
					</span>
				</span>
			))}
		</div>
	);
}

function ChangesDetail({ activity }: { activity: ActivityItem }) {
	const { selectedCurrency } = useCurrencyStore();
	const tree = useMemo(() => {
		const meta = activity.metadata as Record<string, unknown> | null;
		const changes = meta?.changes;
		return buildScopeTree(Array.isArray(changes) ? (changes as ChangeItem[]) : []);
	}, [activity.metadata]);

	if (tree.fields.length === 0 && tree.children.length === 0) return null;

	const asTree = shouldRenderAsTree(tree);
	const crumbs = asTree ? [] : findFieldPath(tree);
	const scoped = crumbs[crumbs.length - 1];
	const flatPriorless = tree.isNew || crumbs.some((node) => node.isNew);

	return (
		<div className="mt-2 flex min-w-0 flex-col gap-2">
			{/* Changes on the activity's own subject sit above any scoped ones. */}
			<FieldGrid fields={tree.fields} currency={selectedCurrency} priorless={tree.isNew} />

			{asTree
				? tree.children.map((child) => (
						<ScopeGroup
							key={child.key}
							node={child}
							depth={0}
							currency={selectedCurrency}
							inheritedNew={tree.isNew}
						/>
					))
				: scoped && (
						<div className="flex min-w-0 flex-col gap-1.5">
							<ScopeCrumbs crumbs={crumbs} />
							<FieldGrid
								fields={scoped.fields}
								currency={selectedCurrency}
								priorless={flatPriorless}
							/>
						</div>
					)}
		</div>
	);
}

/**
 * Plain item list emitted by creation/acceptance events (`metadata.items`).
 * These are not transitions — a creation has no prior state — so they render as
 * a simple "Name — 100 pcs" list rather than a from→to change.
 */
function ItemsDetail({ activity }: { activity: ActivityItem }) {
	const meta = activity.metadata as Record<string, unknown> | null;
	const items = meta?.items as Array<{ name?: string; quantity?: number }> | undefined;
	if (!Array.isArray(items) || items.length === 0) return null;

	return (
		<div className="mt-2.5 flex flex-col gap-1">
			{items.map((item, i) => (
				<div key={i} className="flex items-baseline gap-1.5 text-xs">
					<span className="text-text-2">{item.name ?? 'Item'}</span>
					{item.quantity != null && (
						<span className="font-mono text-text-4">
							— {item.quantity.toLocaleString()} pcs
						</span>
					)}
				</div>
			))}
		</div>
	);
}

/* ── Shared presentational timeline ── */

type ActivityTimelineProps = {
	activities: ActivityItem[];
	loading: boolean;
	/** Resolve the node icon/colors for a given activity action. */
	resolveStyle: (action: string) => ActivityNodeStyle;
	/**
	 * Cap on the scrolling body. A single offer event can run past 1000px, and the
	 * card sits in a `sticky top-0` rail — once the rail outgrows the viewport it
	 * stops sticking, so the cap keeps the rail usable rather than just tidy.
	 */
	maxBodyHeight?: string;
};

export function ActivityTimeline({
	activities,
	loading,
	resolveStyle,
	maxBodyHeight = 'min(900px, 78vh)',
}: ActivityTimelineProps) {
	const bodyRef = useRef<HTMLDivElement>(null);
	const [canScrollDown, setCanScrollDown] = useState(false);

	// macOS hides overlay scrollbars until you scroll, so without this the cut-off
	// content reads as the end of the list.
	const syncScrollState = useCallback(() => {
		const el = bodyRef.current;
		if (!el) return;
		setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
	}, []);

	useEffect(syncScrollState, [syncScrollState, activities.length, loading]);

	const groups = useMemo(() => {
		const map = new Map<string, ActivityItem[]>();
		for (const a of activities) {
			const group = a.createdAt ? getDateGroup(a.createdAt) : 'Unknown';
			if (!map.has(group)) map.set(group, []);
			map.get(group)!.push(a);
		}
		return Array.from(map.entries());
	}, [activities]);

	return (
		<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
			{/* Header */}
			<div
				className="flex shrink-0 items-center px-4 py-4"
				style={{ borderBottom: '0.5px solid var(--line-1)' }}
			>
				<div className="flex items-baseline gap-2.5">
					<span className="text-sm font-medium uppercase tracking-[0.04em] text-text-1">
						Activity
					</span>
					<span className="font-mono text-xs text-text-4">{activities.length}</span>
				</div>
			</div>

			{/* Body */}
			<div className="relative min-h-0">
				<div
					ref={bodyRef}
					onScroll={syncScrollState}
					className="scroll-thin overflow-y-auto overscroll-contain"
					style={{ padding: '4px 18px 16px', maxHeight: maxBodyHeight }}
				>
					{loading ? (
						<div className="flex flex-col gap-4 py-3">
							{[1, 2, 3].map((i) => (
								<div key={i} className="flex items-start gap-3">
									<div className="h-7 w-7 animate-pulse rounded-full bg-surface-fill" />
									<div className="flex-1">
										<div className="mb-1.5 h-3.5 w-32 animate-pulse rounded bg-surface-fill" />
										<div className="h-3 w-20 animate-pulse rounded bg-surface-fill" />
									</div>
								</div>
							))}
						</div>
					) : activities.length === 0 ? (
						<div className="py-6 text-center text-sm text-text-4">No activity yet</div>
					) : (
						groups.map(([group, groupActivities]) => (
							<div key={group}>
								{/* Day group label — pinned so the date stays readable while scrolling. */}
								<div
									className="sticky top-0 z-10"
									style={{
										paddingTop: 14,
										paddingBottom: 8,
										paddingLeft: 14.4,
										paddingRight: 14.4,
										marginLeft: -14.4,
										marginRight: -14.4,
										background: 'var(--bg-2)',
									}}
								>
									<span className="font-mono text-2xs uppercase tracking-[0.12em] text-text-4">
										{group}
									</span>
								</div>

								{/* Events */}
								{groupActivities.map((activity, i) => {
									const label = activity.message || formatAction(activity.action);
									const actor = getActorName(activity);
									const isLast = i === groupActivities.length - 1;

									return (
										<div key={activity.id} className="flex gap-3">
											{/* Node column */}
											<div className="flex shrink-0 flex-col items-center">
												<TypedNode style={resolveStyle(activity.action)} />
												{!isLast && (
													<div
														className="flex-1"
														style={{
															width: 1.5,
															background: 'var(--line-1)',
															margin: '5px 0',
															borderRadius: 1,
														}}
													/>
												)}
											</div>

											{/* Content column */}
											<div
												className="min-w-0 flex-1"
												style={{
													paddingBottom: isLast ? 2 : 14.4,
													marginTop: 4,
												}}
											>
												{/* Title + timestamp */}
												<div className="flex items-baseline justify-between gap-2.5">
													<span
														className="text-sm font-medium text-text-1"
														style={{ letterSpacing: '-0.005em' }}
													>
														{label}
													</span>
													{activity.createdAt && (
														<span className="shrink-0 whitespace-nowrap font-mono text-xs text-text-4">
															{formatTimestamp(activity.createdAt)}
														</span>
													)}
												</div>

												{/* Structured changes */}
												<ChangesDetail activity={activity} />

												{/* Plain item list (creation / acceptance events) */}
												<ItemsDetail activity={activity} />

												{/* Actor */}
												{actor && (
													<div className="mt-2.5 flex items-center gap-1.5">
														<InitialsAvatar name={actor} size={15} />
														<span className="text-xs text-text-4">
															{actor}
														</span>
													</div>
												)}
											</div>
										</div>
									);
								})}
							</div>
						))
					)}
				</div>

				{canScrollDown && (
					<div
						aria-hidden
						className="pointer-events-none absolute inset-x-0 bottom-0 h-8"
						style={{
							background:
								'linear-gradient(to top, var(--bg-2), color-mix(in srgb, var(--bg-2) 60%, transparent) 45%, transparent)',
						}}
					/>
				)}
			</div>
		</Card>
	);
}
